import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Fast + / − / remove in the cart drawer and on /cart (29/09/2026, owner: "làm cho nhanh lên" - the same
 * treatment as Titanium Wares and WakaPaw).
 *
 * Before: Horizon waited 300 ms, sent /cart/change.js, then the Pillar guard / the Lavender sync read the cart
 * and fixed the gifts (Pillar + with a new gift: ~3.2 s, five cart reads).
 * Now, on a product line (not a gift, not Shipping Protection, not the hidden Wood cart offer):
 *  - the number in the box changes at once, a ring spins on the button, Check out rests (html.st-cart-busy);
 *  - ONE request carries the product and its gifts:
 *      Pillar +      /cart/add.js: the extra Pillar + the gifts it now earns, hung on that line (parent_id may
 *                    name a variant sent in the same request) - planned by the Pillar guard's own planner
 *      diffuser +    /cart/add.js: the extra diffuser + one more Lavender under it (unless the shopper removed
 *                    the Lavender this session - the owner's rule of 14/09)
 *      − / remove    /cart/update.js: the product line and its gift lines together (charged Lavender units go
 *                    first, as in st-free-gift.js); removing a line takes its child gifts with it (Shopify)
 *      other lines   /cart/update.js for that line (oils carry _bundle properties, so never add.js)
 *  - quick clicks queue: one request at a time (two cart writes in flight = last write wins on the whole cart);
 *    an answer overtaken by a newer click is rejected with AbortError so Horizon does not repaint backwards.
 * The Pillar guard and the Lavender sync still run after every change as the safety net; with the cart already
 * right they find nothing to do.
 */
if (!window.__stFastCart) {
  window.__stFastCart = true;

  const PILLAR = 'waterless-cold-air-diffuser-large-room';
  const LAVENDER = 'lavender-free-gift';
  // 02/10/2026: CalmRide earns a Peppermint instead of the Lavender
  const PEPPERMINT = 'peppermint-free-gift';
  const GIFTS = [LAVENDER, PEPPERMINT];
  const giftFor = (it) => (it.handle === 'calmride-ultrasonic-aromatherapy-diffuser' ? PEPPERMINT : LAVENDER);
  const giftAttr = (g) => (g === PEPPERMINT ? '_st_gift_dismissed_peppermint' : '_st_gift_dismissed');
  const giftSuffix = (g) => (g === PEPPERMINT ? ':peppermint' : '');
  const CART_JSON = 'st-cart-json'; // {{ cart | json }} (sections/st-cart-json.liquid)
  const PARENTS = 'st-cart-parents'; // line key -> parent key (Liquid's cart json has no parent_relationship)
  const root = document.documentElement;

  const style = document.createElement('style');
  style.textContent =
    "cart-items-component .st-busy{position:relative;color:transparent!important}" +
    "cart-items-component .st-busy svg{opacity:0}" +
    "cart-items-component .st-busy::after{content:'';position:absolute;inset:0;margin:auto;width:14px;height:14px;border-radius:50%;" +
    "border:2px solid rgb(var(--color-foreground-rgb)/.2);border-top-color:rgb(var(--color-foreground-rgb)/.75);animation:st-spin .7s linear infinite}" +
    "@keyframes st-spin{to{transform:rotate(360deg)}}" +
    "@media (prefers-reduced-motion:reduce){cart-items-component .st-busy::after{animation-duration:2s}}" +
    "cart-items-component .st-removing{opacity:.4;transition:opacity .15s;pointer-events:none}" +
    "html.st-cart-busy [name=checkout]{opacity:.6;transition:opacity .15s}";
  document.head.appendChild(style);

  let view = null; // latest cart this script saw, /cart.js shape with parent_relationship
  const keyVariant = new Map(); // every line key ever seen -> variant (rows keep old keys until the repaint)
  const want = new Map(); // variant of a top-level line -> quantity the shopper wants
  // 30/09/2026: variant -> key of the row that was clicked. One diffuser can sit on two top-level lines (Shopify
  // splits the line when the Lavender under it is lowered below the diffuser count); the click must change the
  // line the shopper clicked, not the first line of that variant. Keys change after a write, so the variant
  // stays the queue key and this is only a hint (falls back to the first line).
  const wantKey = new Map();
  // variants asked for with no line in the cart yet (the real product of a Pillar gift, rule #2)
  const newLine = new Set();
  let pumping = null;

  const remember = (cart) => {
    if (!cart?.items) return;
    view = cart;
    for (const item of cart.items) keyVariant.set(item.key, item.variant_id);
  };
  const topLine = (cart, variant) => (cart?.items || []).find((i) => i.variant_id === variant && !i.parent_relationship);
  const childrenOf = (cart, key) => (cart?.items || []).filter((i) => i.parent_relationship?.parent_key === key);
  const isDiffuser = (it) => String(it.product_type || '').startsWith('Diffusers >') && it.handle !== PILLAR;
  const sectionIds = () => [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  const post = async (url, body) => {
    const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok || data?.status) throw new Error(data?.description || data?.message || `${url} failed`);
    return data;
  };
  const readSection = (html, attr) => {
    const m = new RegExp(`<script[^>]*${attr}[^>]*>([\\s\\S]*?)<\\/script>`).exec(html || '');
    try {
      return m ? JSON.parse(m[1]) : null;
    } catch {
      return null;
    }
  };
  /** the whole cart from an add.js answer: st-cart-json + the parent keys */
  const cartFromAnswer = (res, token) => {
    const cart = readSection(res.sections?.[CART_JSON], 'data-st-cart');
    if (!cart?.items) return null;
    const parents = readSection(res.sections?.[PARENTS], 'data-st-cart-parents') || {};
    cart.token = token;
    cart.items = cart.items.map((i) => (parents[i.key] ? { ...i, parent_relationship: { parent_key: parents[i.key] } } : i));
    return cart;
  };
  const lavenderDismissed = (cart, g = LAVENDER) => {
    try {
      if (sessionStorage.getItem('st-free-gift:dismissed' + giftSuffix(g)) === '1') return true;
    } catch {}
    return cart?.attributes?.[giftAttr(g)] === '1';
  };
  // With the Lavender a child line, removing the last diffuser can empty the cart in one step, and the Lavender sync
  // (it lives in the drawer footer, which an empty cart does not render) never sees "no diffuser": its "the shopper
  // removed the gift" memory would then block the Lavender of the NEXT diffuser. Forget it whenever no diffuser is left.
  const forgetLavender = () => {
    try {
      for (const g of GIFTS) {
        sessionStorage.removeItem('st-free-gift:dismissed' + giftSuffix(g));
        sessionStorage.setItem('st-free-gift:diffusers' + giftSuffix(g), '0:0');
      }
    } catch {}
  };
  const lavenderVariant = (cart, g = LAVENDER) =>
    (cart.items || []).find((i) => i.handle === g)?.variant_id || Number(document.querySelector(`st-free-gift[data-gift="${g}"]`)?.dataset.variant || 0);
  // a click that turned out to change nothing: the boxes it bumped go back to what the cart really holds
  const putBack = (cart) => {
    for (const row of document.querySelectorAll('cart-items-component [data-key]')) {
      const it = (cart?.items || []).find((i) => i.key === row.dataset.key);
      const box = row.querySelector('input[type="number"]');
      if (it && box && !want.has(it.variant_id)) box.value = String(it.quantity);
    }
  };

  // ---- busy state: ring on the clicked button, Check out rests while anything is on its way
  let busyN = 0;
  const busy = (on) => {
    busyN = Math.max(0, busyN + (on ? 1 : -1));
    const active = busyN > 0 || !!pumping || want.size > 0;
    root.classList.toggle('st-cart-busy', active);
    if (!active) {
      for (const el of document.querySelectorAll('.st-busy')) el.classList.remove('st-busy');
      for (const el of document.querySelectorAll('.st-removing')) el.classList.remove('st-removing');
    }
  };

  /** the request for one line: returns {url, body, after(answer) -> {cart, sections}} */
  const build = (cart, line, qty) => {
    const ids = sectionIds();
    const secs = { sections: [...ids, CART_JSON, PARENTS].join(','), sections_url: window.location.pathname };
    const kids = childrenOf(cart, line.key);
    const plain = !line.properties || !Object.keys(line.properties).length;
    const raise = qty > line.quantity;
    const addAnswer = async (res) => ({ cart: cartFromAnswer(res, cart.token), sections: res.sections });
    const updAnswer = async (res) => ({ cart: res, sections: res.sections });

    if (line.handle === PILLAR && window.__stPillar?.el()) {
      const P = window.__stPillar;
      const sim = { ...cart, items: [] };
      for (const i of cart.items) {
        if (i.key === line.key) {
          if (qty > 0) sim.items.push({ ...i, quantity: qty });
        } else if (!(qty === 0 && i.parent_relationship?.parent_key === line.key)) sim.items.push(i);
      }
      const plan = P.planFrom(P.el(), sim);
      const done = () => P.writeState(plan.next);
      if (raise && plain && !Object.keys(plan.updates).length) {
        const items = [{ id: line.variant_id, quantity: qty - line.quantity }];
        for (const a of plan.adds) {
          items.push({ id: a.id, quantity: a.quantity, parent_id: line.variant_id });
          const p = plan.next[a.pair]?.p;
          if (p) {
            p[a.parentVariant] = (p[a.parentVariant] || 0) - a.quantity;
            if (p[a.parentVariant] <= 0) delete p[a.parentVariant];
            p[line.variant_id] = (p[line.variant_id] || 0) + a.quantity;
          }
        }
        return { url: Theme.routes.cart_add_url, body: { items, ...secs }, after: async (res) => (done(), addAnswer(res)) };
      }
      const updates = { ...plan.updates, [line.key]: qty };
      if (qty === 0) for (const k of kids) delete updates[k.key];
      return {
        url: Theme.routes.cart_update_url,
        body: { updates, ...secs },
        after: async (res) => {
          if (plan.adds.length) {
            // the line that held the gifts went: hang them on the Pillar line that is left
            const keyOf = (v) => topLine(res, v)?.key;
            const items = plan.adds.map((a) => ({ id: a.id, quantity: a.quantity, parent_line_key: keyOf(a.parentVariant) })).filter((i) => i.parent_line_key);
            if (items.length) {
              const added = await post(Theme.routes.cart_add_url, { items, ...secs });
              done();
              return addAnswer(added);
            }
          }
          done();
          return updAnswer(res);
        },
      };
    }

    if (isDiffuser(line)) {
      const gift = giftFor(line);
      const lav = kids.filter((k) => k.handle === gift);
      const have = lav.reduce((s, k) => s + k.quantity, 0);
      if (raise && plain) {
        const items = [{ id: line.variant_id, quantity: qty - line.quantity }];
        const lv = lavenderVariant(cart, gift);
        const target = have > 0 || !lavenderDismissed(cart, gift) ? Math.max(have, qty) : have;
        if (lv && target > have) items.push({ id: lv, quantity: target - have, parent_id: line.variant_id });
        return { url: Theme.routes.cart_add_url, body: { items, ...secs }, after: addAnswer };
      }
      const updates = { [line.key]: qty };
      const diffusersLeft = (cart.items || []).filter((i) => isDiffuser(i) && !i.parent_relationship && i.key !== line.key).reduce((s, i) => s + i.quantity, 0) + qty;
      const attrs = diffusersLeft === 0 && (cart.attributes?._st_gift_dismissed || cart.attributes?._st_gift_dismissed_peppermint) ? { attributes: { _st_gift_dismissed: '', _st_gift_dismissed_peppermint: '' } } : {};
      if (diffusersLeft === 0) forgetLavender();
      if (qty > 0 && have > qty) {
        // lower the Lavender under this diffuser to one per unit: charged units first (st-free-gift.js rule)
        let over = have - qty;
        for (const k of [...lav].sort((a, b) => (b.final_line_price > 0) - (a.final_line_price > 0))) {
          if (over <= 0) break;
          const cut = Math.min(over, k.quantity);
          updates[k.key] = k.quantity - cut;
          over -= cut;
        }
      }
      return { url: Theme.routes.cart_update_url, body: { updates, ...attrs, ...secs }, after: updAnswer };
    }

    return { url: Theme.routes.cart_update_url, body: { updates: { [line.key]: qty }, ...secs }, after: updAnswer };
  };

  const pump = () =>
    pumping ||
    (pumping = (async () => {
      try {
        // 30/09/2026: never finish in the same tick. A click that needs no request used to run this whole body
        // synchronously, so the `finally` cleared `pumping` BEFORE the assignment above stored the (finished)
        // promise: `pumping` stayed set, every later click was swallowed and Check out stayed dimmed.
        await null;
        while (want.size) {
          const [variant, qty] = want.entries().next().value;
          want.delete(variant);
          const clicked = wantKey.get(variant);
          wantKey.delete(variant);
          const cart = view || (await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json());
          remember(cart);
          const line = (cart.items || []).find((i) => i.key === clicked && !i.parent_relationship) || topLine(cart, variant);
          let req;
          if (!line && qty > 0 && newLine.has(variant)) {
            // rule #2 (30/09/2026): the real product of a Pillar gift, as a new normal paid line (a CalmRide gets its
            // free Lavender from the Lavender sync, like any diffuser)
            newLine.delete(variant);
            const ids = sectionIds();
            req = {
              url: Theme.routes.cart_add_url,
              body: { items: [{ id: variant, quantity: qty }], sections: [...ids, CART_JSON, PARENTS].join(','), sections_url: window.location.pathname },
              after: async (res) => ({ cart: cartFromAnswer(res, cart.token), sections: res.sections }),
            };
          } else if (!line || line.quantity === qty) {
            newLine.delete(variant);
            putBack(cart);
            continue;
          } else req = build(cart, line, qty);
          const deferred = CartLinesUpdateEvent.createPromise();
          document.body.dispatchEvent(
            new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [line ? { id: line.key, quantity: qty } : { merchandiseId: String(variant), quantity: qty }], promise: deferred.promise })
          );
          try {
            const res = await post(req.url, req.body);
            const { cart: next, sections } = await req.after(res);
            if (next?.items) remember(next);
            else view = null;
            if (next && Number(next.item_count) === 0) emptied(next);
            if (want.size) {
              // a newer click is waiting: this answer is only a step, Horizon must not paint it
              deferred.reject(new DOMException('Superseded by a newer cart update', 'AbortError'));
              continue;
            }
            const drawer = { ...(sections || {}) };
            delete drawer[CART_JSON];
            delete drawer[PARENTS];
            const full = next?.items ? next : await (await fetch(`${Theme.routes.cart_url}.js`)).json();
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(full),
              detail: { items: full.items, source: 'st-fast-cart', sections: drawer, didError: false },
            });
          } catch (error) {
            deferred.reject(error);
            throw error;
          }
        }
      } catch (error) {
        console.warn('[st-fast-cart]', error);
        view = null;
        want.clear();
        wantKey.clear();
        newLine.clear();
      } finally {
        pumping = null;
        busy(false);
      }
    })());

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('cart-items-component button[name="plus"], cart-items-component button[name="minus"], cart-items-component .cart-items__remove');
      if (!button || button.disabled || !view) return; // no cart seen yet: the theme's path
      const row = button.closest('[data-key]');
      const variant = keyVariant.get(row?.dataset.key || '');
      if (!variant) return;
      const item = (view.items || []).find((i) => i.key === row.dataset.key) || topLine(view, variant);
      if (!item) return;
      const kind = button.getAttribute('name') === 'plus' ? 1 : button.getAttribute('name') === 'minus' ? -1 : 0;

      if (item.parent_relationship) {
        // + on a Pillar gift: fine up to what the Pillars earn (combo scaling 30/09/2026 - 4 Pillars earn 2 pillowcases,
        // a shopper who took one off may put it back). Past that (rule #2, 30/09/2026): the gift stays as it is and the
        // extra unit is its REAL product as a normal paid line; a gift with no sellable twin (pillowcase, oil sets,
        // eBooks) does nothing, as before.
        const parent = (view.items || []).find((i) => i.key === item.parent_relationship.parent_key);
        if (kind > 0 && parent?.handle === PILLAR) {
          const P = window.__stPillar;
          const earned = P?.el() ? P.planFrom(P.el(), view).next[item.handle]?.e || 0 : 0;
          const have = (view.items || []).filter((i) => i.handle === item.handle).reduce((s, i) => s + i.quantity, 0);
          if (have >= earned) {
            event.preventDefault();
            event.stopImmediatePropagation();
            const twin = P?.twinOf ? P.twinOf(item.handle) : 0;
            if (twin) {
              const own = topLine(view, twin);
              const current = want.has(twin) ? want.get(twin) : own?.quantity || 0;
              want.set(twin, current + 1);
              if (!own) newLine.add(twin);
              button.classList.add('st-busy');
              root.classList.add('st-cart-busy');
              pump();
            }
          }
        }
        return; // other gift clicks (a Lavender bought on purpose, a Pillar gift below its earned count, removing a gift): the theme's path
      }
      const tags = item.product_type || '';
      if (item.handle === 'shipping-protection' || tags === 'Cart Offer' || /Shipping Protection/.test(tags)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const input = row.querySelector('input[type="number"], input[name="updates[]"]');
      const current = want.has(variant) ? want.get(variant) : Number(input?.value || item.quantity) || 0;
      const qty = kind > 0 ? current + 1 : kind < 0 ? Math.max(1, current - 1) : 0;
      if (qty === current && kind !== 0) return;
      want.set(variant, qty);
      wantKey.set(variant, item.key);
      if (input && qty > 0) input.value = String(qty);
      if (qty === 0) row.classList.add('st-removing');
      // the Lavender box under a diffuser follows at once too
      if (isDiffuser(item)) {
        for (const k of childrenOf(view, item.key)) {
          if (k.handle !== giftFor(item)) continue;
          const box = document.querySelector(`cart-items-component [data-key="${CSS.escape(k.key)}"] input[type="number"]`);
          if (box && kind > 0 && !lavenderDismissed(view, giftFor(item))) box.value = String(Math.max(Number(box.value) || 0, qty));
          if (box && kind < 0 && Number(box.value) > qty) box.value = String(qty);
        }
      }
      button.classList.add('st-busy');
      root.classList.add('st-cart-busy');
      pump();
    },
    true
  );

  // every other cart change (product pages, the guards, apps): keep the cart this script plans from fresh
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.detail?.source === 'st-fast-cart') return;
    busy(true);
    (event.promise || Promise.resolve())
      .then((result) => {
        emptied(result?.cart);
        if (result?.detail?.source === 'st-fast-cart') return;
        view = null; // re-read before the next fast click
        setTimeout(prime, 60);
      })
      .catch(() => {})
      .finally(() => busy(false));
  });

  /** an empty cart keeps its attributes: drop the Lavender "removed" flag so the next diffuser gets its gift */
  let clearing = false;
  function emptied(cart) {
    const count = Number(cart?.totalQuantity ?? cart?.item_count ?? -1);
    if (count !== 0) return;
    forgetLavender();
    if (clearing) return;
    clearing = true;
    fetch(`${Theme.routes.cart_url}.js`)
      .then((r) => r.json())
      .then((c) => (c.item_count === 0 && (c.attributes?._st_gift_dismissed || c.attributes?._st_gift_dismissed_peppermint) ? post(Theme.routes.cart_update_url, { attributes: { _st_gift_dismissed: '', _st_gift_dismissed_peppermint: '' } }) : null))
      .catch(() => {})
      .finally(() => (clearing = false));
  }

  const prime = async () => {
    if (pumping || view) return;
    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      if (!pumping) remember(cart);
    } catch {}
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(prime, 400));
  else setTimeout(prime, 400);
}
